import type { Zero } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { v4 as uuidv4 } from "uuid";

import ClosableCard from "@/components/closable-card";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLogin } from "@/hooks/use-login";
import { useRoom } from "@/hooks/use-room";
import { type Mutators } from "@/zero/mutators";
import { type Schema } from "@/zero/schema";

function createRoom(z: Zero<Schema>, name: string, owner: string) {
  const id = uuidv4();
  z.mutate.room.insert({
    id,
    name,
    createdOn: Date.now(),
    owner,
    status: "created",
  });
}

function closeRoom(z: Zero<Schema>, id: string) {
  z.mutate.room.update({
    id,
    status: "closed",
  });
}

export default function RoomList() {
  const z = useZero<Schema, Mutators>();
  const { user } = useLogin();
  const navigate = useNavigate();
  const { setJoinedRoom } = useRoom();
  const roomQuery = z.query.room
    .where("status", "!=", "closed")
    .related("roomOwner")
    .limit(100); // TODO: pagination
  const [rooms] = useQuery(roomQuery);
  const [roomName, setRoomName] = useState("");

  const handleCreateRoom = () => {
    const trimmedRoomName = roomName.trim();
    if (trimmedRoomName && user?.id) {
      createRoom(z as Zero<Schema>, trimmedRoomName, user.id);
    }
    setRoomName("");
  };

  const handleJoinRoom = async (roomId: string) => {
    // Set the joined room in the hook
    setJoinedRoom(roomId);

    // Navigate to home after joining
    navigate("/");
  };

  return (
    <ClosableCard className="relative" title="Rooms">
      <CardContent className="space-y-4">
        {rooms.length === 0 && <p>No rooms found</p>}
        {rooms.map((room) => (
          <div
            key={room.id}
            className="flex items-center justify-between p-2 border rounded"
          >
            <div>
              <h3 className="font-medium">{room.name}</h3>
              <p className="text-sm text-muted-foreground">
                Status: {room.status} <br />
                Owner: {room.roomOwner?.name}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={room.status === "closed"}
                onClick={() => handleJoinRoom(room.id)}
              >
                Join
              </Button>
              {room.status !== "closed" && room.owner === user?.id && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => closeRoom(z as Zero<Schema>, room.id)}
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        ))}

        <div className="space-y-2">
          <Label htmlFor="room-name">Room Name</Label>
          <Input
            id="room-name"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder="Enter room name"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCreateRoom();
              }
            }}
          />
          <Button
            onClick={handleCreateRoom}
            className="w-full"
            disabled={!roomName.trim()}
          >
            Create New Room
          </Button>
        </div>
      </CardContent>
    </ClosableCard>
  );
}
